<!--ABOUTME: Policy acceptance component for first-time users and policy updates-->
<!--ABOUTME: Used with PrimeVue useDialog for dynamic policy acceptance-->

<template>
    <div class="policy-content">
        <p class="policy-intro">
            {{ introMessage }}
        </p>

        <div class="policy-acceptance">
            <div class="input checkbox">
                <Checkbox
                    id="accept-privacy-policy"
                    v-model="acceptPrivacyPolicy"
                    :binary="true"
                    :class="{ 'p-invalid': submitted && !acceptPrivacyPolicy }"
                />
                <label for="accept-privacy-policy" class="checkbox-label">
                    I accept the
                    <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy
                    </a>
                </label>
            </div>
            <small v-if="submitted && !acceptPrivacyPolicy" class="p-error">
                You must accept the Privacy Policy to continue
            </small>

            <div class="input checkbox">
                <Checkbox
                    id="accept-terms-conditions"
                    v-model="acceptTermsConditions"
                    :binary="true"
                    :class="{
                        'p-invalid': submitted && !acceptTermsConditions,
                    }"
                />
                <label for="accept-terms-conditions" class="checkbox-label">
                    I accept the
                    <a
                        href="/terms-of-use"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Terms of Use
                    </a>
                </label>
            </div>
            <small v-if="submitted && !acceptTermsConditions" class="p-error">
                You must accept the Terms of Use to continue
            </small>
        </div>

        <div class="policy-actions">
            <Button
                label="Accept & Continue"
                icon="pi pi-check"
                @click="handleAccept"
                :loading="loading"
                autofocus
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { Ref, computed, inject, ref } from "vue";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const isNewUser = computed(() => dialogRef?.value.data?.isNewUser || false);

const acceptPrivacyPolicy = ref(false);
const acceptTermsConditions = ref(false);
const submitted = ref(false);
const loading = ref(false);

const introMessage = computed(() => {
    if (isNewUser.value) {
        return "Welcome to MyStekz! Before you can start using the platform, please review and accept our policies:";
    } else {
        return "We have updated our policies. Please review and accept the updated terms to continue:";
    }
});

const handleAccept = async () => {
    submitted.value = true;

    if (!acceptPrivacyPolicy.value || !acceptTermsConditions.value) {
        return;
    }

    loading.value = true;

    try {
        if (dialogRef?.value) {
            dialogRef.value.close({
                accepted: true,
                acceptPrivacyPolicy: acceptPrivacyPolicy.value,
                acceptTermsConditions: acceptTermsConditions.value,
            });
        } else {
            console.error("Dialog reference not available");
        }
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.policy-content {
    .policy-intro {
        margin-bottom: 2rem;
        color: var(--text-color);
        line-height: 1.6;
        font-size: 1rem;
    }

    .policy-acceptance {
        .input.checkbox {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            margin-bottom: 1.5rem;

            .checkbox-label {
                font-size: 0.9rem;
                line-height: 1.4;
                color: var(--text-color);

                a {
                    color: var(--primary-color);
                    text-decoration: underline;

                    &:hover {
                        text-decoration: none;
                    }
                }
            }
        }
    }

    .policy-actions {
        margin-top: 2rem;
        display: flex;
        justify-content: flex-end;
        padding-top: 1rem;
        border-top: 1px solid var(--surface-border);
    }
}
</style>
